import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * PlantScanner Component
 * 
 * Camera-based plant identification
 * - Native browser camera API
 * - Capture photo
 * - Upload & match with database
 * - Display results
 */
const PlantScanner = () => {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const [stream, setStream] = useState(null);
    const [cameraActive, setCameraActive] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [matches, setMatches] = useState([]);
    const [error, setError] = useState(null);
    const [permissionDenied, setPermissionDenied] = useState(false);

    useEffect(() => {
        // Auto-start camera on mount
        startCamera();

        return () => {
            stopCamera();
        };
    }, []);

    const startCamera = async () => {
        try {
            setError(null);
            setPermissionDenied(false);

            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment', // Rückkamera auf Mobilgeräten
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                setStream(mediaStream);
                setCameraActive(true);
            }
        } catch (err) {
            console.error('Camera error:', err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setPermissionDenied(true);
                setError('Kamera-Zugriff wurde verweigert. Bitte erlaube den Kamera-Zugriff in deinen Browser-Einstellungen.');
            } else if (err.name === 'NotFoundError') {
                setError('Keine Kamera gefunden. Bitte stelle sicher, dass dein Gerät eine Kamera hat.');
            } else {
                setError('Kamera konnte nicht gestartet werden: ' + err.message);
            }
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
            setCameraActive(false);
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        // Set canvas size to video size
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw current video frame to canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to blob
        canvas.toBlob((blob) => {
            const imageUrl = URL.createObjectURL(blob);
            setCapturedImage({ blob, url: imageUrl });
            stopCamera();
        }, 'image/jpeg', 0.8);
    };

    const retakePhoto = () => {
        if (capturedImage) {
            URL.revokeObjectURL(capturedImage.url);
        }
        setCapturedImage(null);
        setMatches([]);
        setError(null);
        startCamera();
    };

    const uploadAndScan = async () => {
        if (!capturedImage) return;

        setScanning(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('image', capturedImage.blob, 'plant-scan.jpg');

            const response = await fetch('/api/plants/scan/', {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Scan fehlgeschlagen');
            }

            const data = await response.json();
            setMatches(data.matches || []);

            if (data.matches.length === 0) {
                setError('Keine passende Pflanze gefunden. Versuche es mit einem klareren Foto.');
            }
        } catch (err) {
            console.error('Scan error:', err);
            setError('Fehler beim Scannen: ' + err.message);
        } finally {
            setScanning(false);
        }
    };

    const openPlantDetail = (plantId) => {
        navigate(`/?plant=${plantId}`);
    };

    const renderStars = (confidence) => {
        const stars = Math.round(confidence * 5);
        return '★'.repeat(stars) + '☆'.repeat(5 - stars);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                <div className="container mx-auto px-4 py-4 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-plantlix-600 dark:hover:text-plantlix-400 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="font-semibold">Zurück</span>
                    </button>

                    <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        📸 Plant Scanner
                    </h1>

                    <div className="w-20"></div> {/* Spacer for centering */}
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-6 max-w-2xl">
                {/* Error Message */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-red-800 dark:text-red-200 text-sm">
                            {error}
                        </p>
                        {permissionDenied && (
                            <button
                                onClick={startCamera}
                                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                            >
                                Erneut versuchen
                            </button>
                        )}
                    </div>
                )}

                {/* Camera View */}
                {cameraActive && !capturedImage && (
                    <div className="relative bg-black rounded-xl overflow-hidden aspect-[3/4] mb-4">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                        />

                        {/* Crosshair Guide */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-64 h-64 border-2 border-white/50 rounded-lg">
                                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-plantlix-500"></div>
                                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-plantlix-500"></div>
                                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-plantlix-500"></div>
                                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-plantlix-500"></div>
                            </div>
                        </div>

                        {/* Guide Text */}
                        <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                            <p className="text-white text-center text-sm">
                                Richte die Pflanze im Rahmen aus
                            </p>
                        </div>
                    </div>
                )}

                {/* Captured Image Preview */}
                {capturedImage && !matches.length && (
                    <div className="relative bg-black rounded-xl overflow-hidden aspect-[3/4] mb-4">
                        <img
                            src={capturedImage.url}
                            alt="Captured plant"
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}

                {/* Hidden Canvas for capturing */}
                <canvas ref={canvasRef} className="hidden" />

                {/* Action Buttons */}
                {cameraActive && !capturedImage && (
                    <button
                        onClick={capturePhoto}
                        className="w-full py-4 bg-plantlix-600 hover:bg-plantlix-700 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-colors shadow-lg"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Foto aufnehmen
                    </button>
                )}

                {capturedImage && !matches.length && (
                    <div className="space-y-3">
                        <button
                            onClick={uploadAndScan}
                            disabled={scanning}
                            className="w-full py-4 bg-plantlix-600 hover:bg-plantlix-700 disabled:bg-gray-400 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-colors shadow-lg"
                        >
                            {scanning ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Wird gescannt...
                                </>
                            ) : (
                                <>
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    Pflanze identifizieren
                                </>
                            )}
                        </button>

                        <button
                            onClick={retakePhoto}
                            disabled={scanning}
                            className="w-full py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl font-semibold transition-colors"
                        >
                            Neues Foto aufnehmen
                        </button>
                    </div>
                )}

                {/* Match Results */}
                {matches.length > 0 && (
                    <div className="space-y-4">
                        <div className="bg-plantlix-50 dark:bg-plantlix-900/20 border border-plantlix-200 dark:border-plantlix-800 rounded-lg p-4">
                            <p className="text-plantlix-800 dark:text-plantlix-200 font-semibold text-center">
                                🎯 {matches.length} Treffer gefunden!
                            </p>
                        </div>

                        {matches.map((match, index) => (
                            <div
                                key={match.plant_id || index}
                                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                            >
                                <div className="p-4">
                                    {/* Confidence */}
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-2xl text-yellow-500">
                                            {renderStars(match.confidence)}
                                        </span>
                                        <span className="text-sm font-bold text-plantlix-600 dark:text-plantlix-400">
                                            {Math.round(match.confidence * 100)}% Match
                                        </span>
                                    </div>

                                    {/* Plant Image */}
                                    {match.thumbnail && (
                                        <img
                                            src={match.thumbnail}
                                            alt={match.german_name}
                                            className="w-full h-48 object-cover rounded-lg mb-3"
                                        />
                                    )}

                                    {/* Plant Info */}
                                    <div className="space-y-1 mb-4">
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                                            {match.german_name || match.name}
                                        </h3>
                                        <p className="text-sm italic text-gray-600 dark:text-gray-400">
                                            {match.name}
                                        </p>
                                        {match.family && (
                                            <p className="text-xs text-gray-500 dark:text-gray-500">
                                                Familie: {match.family}
                                            </p>
                                        )}
                                    </div>

                                    {/* Action Button */}
                                    <button
                                        onClick={() => openPlantDetail(match.plant_id)}
                                        className="w-full py-3 bg-plantlix-600 hover:bg-plantlix-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                                    >
                                        Details ansehen
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}

                        {/* Retry Button */}
                        <button
                            onClick={retakePhoto}
                            className="w-full py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl font-semibold transition-colors"
                        >
                            📸 Nochmal scannen
                        </button>
                    </div>
                )}

                {/* Info Text */}
                {!capturedImage && !matches.length && cameraActive && (
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">
                            💡 Tipps für beste Ergebnisse:
                        </h3>
                        <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                            <li>• Fotografiere die Pflanze bei gutem Licht</li>
                            <li>• Achte auf scharfe, klare Bilder</li>
                            <li>• Zeige charakteristische Merkmale (Blätter, Blüten)</li>
                            <li>• Halte das Gerät ruhig</li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlantScanner;
